import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IChangeCheckboxEventArgs, IgxDialogComponent, ISelectionEventArgs } from '@infragistics/igniteui-angular';
import { ConditionType } from 'src/app/models/enums/conditionType';
import { PackageState } from 'src/app/models/enums/packageState';
import { Level } from 'src/app/models/level';
import { Package } from 'src/app/models/package';
import { PackageRequest } from 'src/app/models/package-request';
import { Participation } from 'src/app/models/participation';
import { Period } from 'src/app/models/period';
import { Product } from 'src/app/models/product';
import { Sport } from 'src/app/models/sport';
import { MessagesService } from 'src/app/services/messages.service';
import { ProgramsService } from 'src/app/services/programs.service';
import { WaitIndicatorService } from 'src/app/services/wait-indicator.service';
import { Kid } from '../../models/kid';
import { Program } from '../../models/program';
import { updateCollection } from 'src/app/shared/utils';

interface DateDescriptor {
    date: Date;
    packageDates: { date: Date, selected: boolean, netPrice?: number, grossPrice?: number, deposit: number }[];
    period: Period | undefined;
    showDetails: boolean;
    sports: Sport[];
    levels: Level[];
    sport?: Sport;
    level?: Level;
    discount: number;
    netPrice?: number;
    grossPrice?: number;
    deposit: number;
    depositAmount: number;
    dateIsSelected: boolean;
}

@Component({
    selector: 'app-add-package',
    templateUrl: './add-package.component.html',
    styleUrls: ['./add-package.component.scss']
})
export class AddPackageComponent {
    private _program?: Program;
    private _request: PackageRequest | undefined;
    private _requestedProgram?: Program;
    private _kid?: Kid;
    private _product: Product | undefined;
    private _sport: Sport | undefined;
    private _level: Level | undefined;
    private _sports: Sport[] = [];
    private _levels: Level[] = [];
    private _discount: number = 0;
    private _participations: Participation[] = [];
    private _withFriends: boolean = false;
    private _withSiblings: boolean = false;
    private _editPart: DateDescriptor | undefined;

    protected kidRequestedSport?: Sport;
    protected kidRequestedLevel?: Level;
    protected requestedSport?: Sport;
    protected requestedLevel?: Level;
    protected totalPrice: number = 0;
    protected totalDeposit: number = 0;
    protected products: Product[] = [];
    protected sports: Sport[] = [];
    protected levels: Level[] = [];
    protected requestedDates: DateDescriptor[] = [];
    protected requestedPeriodDates: DateDescriptor[] = [];
    protected dialogTitle = '';
    protected inputLabel = '';
    protected editValue = 0;

    protected get withFriends(): boolean {
        return this._withFriends;
    }
    protected set withFriends(v: boolean) {
        this._withFriends = v;
        this.discount = this.calculateDiscount();
    }

    protected get withSiblings(): boolean {
        return this._withSiblings;
    }
    protected set withSiblings(v: boolean) {
        this._withSiblings = v;
        this.discount = this.calculateDiscount();
    }

    @Output() public done = new EventEmitter<Package[]>();

    public allPrograms: Program[] = [];
    public programs: Program[] = [];

    public get discount(): number {
        return this._discount;
    }
    public set discount(v: number) {
        this._discount = v;
        this.updateDiscounts();
        this.updatePrices(this.requestedDates);
        this.updatePrices(this.requestedPeriodDates);
        this.totalPrice = this.calculateTotalPrice(this.requestedDates, this.requestedPeriodDates);
        this.totalDeposit = this.calculateTotalDeposit(this.requestedDates, this.requestedPeriodDates);
    }

    public get request(): PackageRequest | undefined { return this._request; }
    @Input()
    public set request(v: PackageRequest | undefined) {
        this._request = v;
        if (v) {
            this.init()
        }
    }

    public get kid(): Kid | undefined { return this._kid; }
    @Input()
    public set kid(v: Kid | undefined) {
        this._kid = v;
        if (v) {
            this.init()
        }
    }

    public get requestedProgram(): Program | undefined { return this._requestedProgram; }
    @Input()
    public set requestedProgram(v: Program | undefined) {
        this._requestedProgram = this.allPrograms.find(p => p.id === v?.id?.replaceAll('-', ''));
        this.program = this.requestedProgram;
    }

    public get program(): Program | undefined { return this._program; }
    @Input()
    public set program(v: Program | undefined) {
        this._program = this.allPrograms.find(p => p.id === v?.id?.replaceAll('-', ''));
        this.updateProgram(v?.id);
    }

    public get product(): Product | undefined { return this._product; }
    @Input()
    public set product(v: Product | undefined) {
        this._product = v;
        this.updateDates();
    }

    public get sport(): Sport | undefined {
        return this._sport;
    }
    @Input()
    public set sport(v: Sport | undefined) {
        this._sport = v;
        this._level = undefined;
        this.levels = [];
        if (v) {
            this.populateLevels(v);
        }
        this.updateDates();
    }

    public get level(): Level | undefined {
        return this._level;
    }
    @Input()
    public set level(v: Level | undefined) {
        this._level = v;
        this.updateDates();
    }

    public clear = (): void => {
        this.request = undefined;
        this.requestedProgram = undefined;
        this.kid = undefined;
        this.requestedDates = [];
        this.requestedPeriodDates = [];
        this.discount = 0;
        this.withFriends = false;
        this.withSiblings = false;
        this.totalPrice = 0;
        this.program = undefined;
        this.product = undefined;
        this.sport = undefined;
        this.level = undefined;
        this.programs = [];
        this.products = [];
        this.sports = [];
        this.levels = [];
    }

    protected calculateKidAge = (kid: Kid): string => {
        if (!kid?.birthDate) {
            return 'Липсва дата на раждане';
        }
        const today = new Date();
        let age = today.getFullYear() - kid.birthDate.getFullYear();
        const m = today.getMonth() - kid.birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < kid.birthDate.getDate())) {
            age--
        }
        return `${age} г.`;
    }

    protected capacityFree = (period: Period, date: Date | undefined): number => {
        let occupied = 0;
        if (date) {
            occupied = this._participations.filter(p => p.package?.period === period.id && p.date.getTime() === date.getTime()).length;
        } else {
            for (const date of period.dates) {
                let current = this._participations.filter(p => p.package?.period === period.id && p.date.getTime() === date.getTime()).length;
                occupied = Math.max(occupied, current);
            }
        }
        return period.capacity - occupied;
    }

    protected getPeriodLevels = (period: Period): string => {
        return period.levels.map(l => l.name).join(', ') ?? 'Х';
    }

    protected getPeriodAge = (period: Period): string => {
        return `${period.ageRange.lower}-${period.ageRange.upper} г.`;
    }

    protected startUpdatePart = (dialog: IgxDialogComponent, part: DateDescriptor, type: string): void => {
        this._editPart = part;
        switch (type) {
            case 'grossPrice':
                this.dialogTitle = 'Обнови цена';
                this.inputLabel = 'Цена';
                this.editValue = Math.round(this._editPart.grossPrice ?? 0);
                break;
            case 'discount':
                this.dialogTitle = 'Обнови отстъпката';
                this.inputLabel = 'Отстъпка';
                this.editValue = Math.round(this._editPart.discount ?? 0);
                break;
            case 'deposit':
                this.dialogTitle = 'Обнови депозит';
                this.inputLabel = 'Депозит';
                this.editValue = Math.round(this._editPart.deposit ?? 0);
                break;
            case 'depositAmount':
                this.dialogTitle = 'Обнови сума депозит';
                this.inputLabel = 'Сума депозит';
                this.editValue = Math.round(this._editPart.depositAmount ?? 0);
                break;
            default:
                return;
        }

        dialog.open();
    }

    protected endUpdatePart = (dialog: IgxDialogComponent, update: boolean): void => {
        if (update && this._editPart) {
            let update = true;
            switch (this.dialogTitle) {
                case 'Обнови цена':
                    this._editPart.grossPrice = Math.round(this.editValue);
                    break;
                case 'Обнови отстъпката':
                    this._editPart.discount = Math.round(this.editValue);
                    break;
                case 'Обнови депозит':
                    this._editPart.deposit = Math.round(this.editValue * 100 + Number.EPSILON) / 100;
                    this._editPart.depositAmount = this.reducePrice(this._editPart.grossPrice!, 100 - this._editPart.deposit);
                    update = false;
                    break;
                case 'Обнови сума депозит':
                    this._editPart.depositAmount = Math.round(this.editValue);
                    this._editPart.deposit = Math.round((this._editPart.depositAmount * 100 / this._editPart.grossPrice! + Number.EPSILON) * 100) / 100;
                    update = false;
                    break;
            }

            if (update) {
                this.updatePrices(this.requestedDates);
                this.updatePrices(this.requestedPeriodDates);
            }

            this.totalPrice = this.calculateTotalPrice(this.requestedDates, this.requestedPeriodDates);
            this.totalDeposit = this.calculateTotalDeposit(this.requestedDates, this.requestedPeriodDates);
        }

        this._editPart = undefined
        this.dialogTitle = '';
        this.inputLabel = '';
        this.editValue = 0;
        dialog.close();
    }

    protected calculateDiscount = (): number => {
        let result = 0;
        if (!this.requestedProgram || !this.kid) {
            return result;
        }
        let discounts = this.program!.discounts;
        discounts = discounts.filter(d => d.conditionType !== ConditionType.Date || d.date!.getTime() > new Date().getTime());
        discounts = discounts.filter(d => d.conditionType !== ConditionType.Friend || this.withFriends);
        discounts = discounts.filter(d => d.conditionType !== ConditionType.Sibling || this.withSiblings);
        discounts = discounts.filter(d => d.conditionType !== ConditionType.OldCustomer || this.kid?.packages.some(p => p.payments.length > 0));

        const summable = discounts.filter(d => d.summable);
        const nonSummable = discounts.filter(d => !d.summable);
        result = summable.reduce((sum, d) => sum + d.value, 0);
        result = Math.max(...nonSummable.map(d => d.value), result);

        return result;
    }

    protected periodSportSelected = (e: ISelectionEventArgs): void => {
        this.sport = this.sports.find(s => s.id === e.newSelection.value.replaceAll('-', ''));
        this.levels = this.sport?.levels ?? [];
        this.level = undefined;
        this.requestedPeriodDates.forEach(d => {
            d.period = undefined;
            d.packageDates = [];
        });
        this.totalPrice = 0;
    }

    protected periodLevelSelected = (e: ISelectionEventArgs): void => {
        this.level = this.levels.find(l => l.id === e.newSelection.value.replaceAll('-', ''));
        this.requestedPeriodDates.forEach(d => {
            d.period = this.level?.periods.find(p => p.dates.some(pd => pd.getTime() === d.date.getTime()));
            if (d.period) {
                d.period.dates.forEach(pd => d.packageDates.push({
                    date: pd,
                    selected: true,
                    deposit: d.period?.deposit ?? 0,
                }));
            }
        });
        this.updateDiscounts();
        this.updatePrices(this.requestedPeriodDates);
        this.totalPrice = this.calculateTotalPrice(this.requestedDates, this.requestedPeriodDates);
        this.totalDeposit = this.calculateTotalDeposit(this.requestedDates, this.requestedPeriodDates);
    }

    protected periodCheckedChange = (e: IChangeCheckboxEventArgs, d: DateDescriptor): void => {
        for (const pd of d.packageDates) {
            pd.selected = e.checked;
        }
        this.requestedPeriodDates = [...this.requestedPeriodDates];
        d.dateIsSelected = e.checked;
        this.totalPrice = this.calculateTotalPrice(this.requestedDates, this.requestedPeriodDates);
        this.totalDeposit = this.calculateTotalDeposit(this.requestedDates, this.requestedPeriodDates);
    }

    protected isChecked = (d: DateDescriptor): boolean => {
        for (const date of d.packageDates) {
            if (!date.selected) {
                return false;
            }
        }
        return true;
    }

    protected isIndeterminate = (d: DateDescriptor): boolean => {
        let count = 0;
        for (const date of d.packageDates) {
            if (date.selected) {
                count++;
            }
        }
        return count > 0 && count !== d.packageDates.length;
    }

    protected periodDateCheckedChange = (e: IChangeCheckboxEventArgs, pd: { date: Date, selected: boolean }, parent: DateDescriptor): void => {
        pd.selected = e.checked;
        parent.dateIsSelected = parent.packageDates.some(pd => pd.selected);
        this.updatePrices(this.requestedPeriodDates);
        this.totalPrice = this.calculateTotalPrice(this.requestedDates, this.requestedPeriodDates);
        this.totalDeposit = this.calculateTotalDeposit(this.requestedDates, this.requestedPeriodDates);
    }

    protected dateSportSelected = (e: ISelectionEventArgs): void => {
        this.sport = this.sports.find(s => s.id === e.newSelection.value.replaceAll('-', ''));
        this.levels = this.sport?.levels ?? [];
        this.level = undefined;
        this.requestedDates.forEach(d => {
            d.period = undefined;
        });
        this.totalPrice = 0;
    }

    protected dateLevelSelected = (e: ISelectionEventArgs): void => {
        this.level = this.levels.find(l => l.id === e.newSelection.value.replaceAll('-', ''));
        this.requestedDates.forEach(d => {
            d.period = this.level?.periods.find(p => p.dates.some(pd => pd.getTime() === d.date.getTime()));
        });
        this.updateDiscounts();
        this.updatePrices(this.requestedDates);
        this.totalPrice = this.calculateTotalPrice(this.requestedDates, this.requestedPeriodDates);
        this.totalDeposit = this.calculateTotalDeposit(this.requestedDates, this.requestedPeriodDates);
    }

    protected dateCheckedChange = (e: IChangeCheckboxEventArgs, d: DateDescriptor): void => {
        d.dateIsSelected = e.checked;
        this.totalPrice = this.calculateTotalPrice(this.requestedDates, this.requestedPeriodDates);
        this.totalDeposit = this.calculateTotalDeposit(this.requestedDates, this.requestedPeriodDates);
    }

    protected generatePackageDescription = (e: Date): string => {
        const period = this.program?.periods.find(p => p.dates[0].getTime() === e.getTime());
        return `Пакет - ${period?.description}`;

    }

    protected create = async () => {
        if (!this.program || !this.kid)
            return;

        const parts: {
            period: string,
            date: string,
            netPrice: number,
            grossPrice: number,
            discount: number,
            depositAmount: number,
            depositDueDate: string,
        }[] = [];
        const depositDueDate = new Date(new Date().getTime() + (this.program.depositDueDays ?? 0) * 24 * 60 * 60 * 1000);
        for (const dateDescriptor of this.requestedPeriodDates) {
            for (const pd of dateDescriptor.packageDates.filter(pd => pd.selected)) {
                parts.push({
                    period: dateDescriptor.period!.id!,
                    date: pd.date.toISOString().split('T')[0],
                    netPrice: pd.netPrice!,
                    grossPrice: pd.grossPrice!,
                    discount: dateDescriptor.discount,
                    depositAmount: dateDescriptor.depositAmount,
                    depositDueDate: depositDueDate.toISOString().split('T')[0]
                });
            }
        }
        for (const dateDescriptor of this.requestedDates.filter(d => d.dateIsSelected)) {
            parts.push({
                period: dateDescriptor.period!.id!,
                date: dateDescriptor.date.toISOString().split('T')[0],
                netPrice: dateDescriptor.netPrice!,
                grossPrice: dateDescriptor.grossPrice! / dateDescriptor.period!.packageDuration,
                discount: dateDescriptor.discount,
                depositAmount: dateDescriptor.depositAmount,
                depositDueDate: depositDueDate.toISOString().split('T')[0]
            });
        }
        let packages: Package[] = [];
        try {
            this.wait.show();
            packages = await this.programService.addPackage(parts, this.program.id!, this.kid.id!);
            const participations = packages.flatMap(p => p.participations);
            for (const p of participations) {
                this._participations = updateCollection(p, this._participations);
            }
            if (this.request) {
                this.request.state = PackageState.Done
                await this.programService.packageRequestToDone(this.request.id?.replaceAll('-', '')!);
            }

            if (this.request) {
                this.msgs.success('Заявката е обработена!');
            } else {
                this.msgs.success(`${this.kid.name} е успешно записано за ${this.program.name}`);
            }
        } catch (error) {
            console.error(error);
            this.msgs.error('Възникна грешка. Заявката не е обработена!');
        } finally {
            this.clear();
            this.wait.hide();
            this.done.emit(packages);
        }
    }

    protected cancel = (): void => {
        this.done.emit([]);
        this.clear();
    }

    constructor(
        private programService: ProgramsService,
        private wait: WaitIndicatorService,
        private msgs: MessagesService,
    ) { }

    public loadData = async () => {
        const data = await this.programService.packageData();
        this.allPrograms = data.programs;
        this._sports = data.sports;
        this._levels = data.levels;
    }

    private updateProgram = async (program: string | undefined) => {
        if (program == null) return;

        this.wait.show();
        const data = await this.programService.updateProgramById(program);
        this._participations = data.participations;
        this._program = data.program;
        updateCollection(this._program, this.allPrograms);
        this.init();
        this.wait.hide();
    }

    private init = (): void => {
        if (!this.kid)
            return;

        if (!this.program)
            return;

        if (!this.program.periods)
            return;

        this.programs = this.allPrograms.filter(p => !p.deleted);
        if (this.requestedProgram && !this.programs.find(p => p.id === this.requestedProgram!.id)) {
            this.programs = [...this.programs, this.requestedProgram!]
        }

        this.requestedDates = [];
        this.requestedPeriodDates = [];
        this.product = undefined;
        this.sport = undefined;
        this.level = undefined;
        this.products = [];
        this.sports = [];
        this.levels = [];

        this.populateProducts(this.program.periods);
        this.populateSports(this.program.periods);

        if (this.request && this.program.id === this.requestedProgram?.id) {
            this.kidRequestedSport = this.kid.sports.find(s => s.sport?.id === this.request?.sport?.replaceAll('-', ''))?.sport;
            this.kidRequestedLevel = this.kid.sports.find(s => s.sport?.id === this.request?.sport?.replaceAll('-', ''))?.level;
            this.requestedSport = this._sports.find(s => s.id === this.request?.sport?.replaceAll('-', ''));
            this.requestedLevel = this._levels.find(l => l.id === this.request?.level?.replaceAll('-', ''));
            if (this.request.product) {
                this.product = this.products.find(p => p.name === this.request!.product.replaceAll('-', ''));
            }
        }

        if (this.requestedSport && this.program.id === this.requestedProgram?.id) {
            this.sport = this.sports.find(s => s.id === this.requestedSport!.id?.replaceAll('-', ''));
            if (this.sport && this.requestedLevel) {
                this.level = this.levels.find(l => l.id === this.requestedLevel!.id?.replaceAll('-', ''));
            }
        }

        this.updateDates();
        this.discount = this.calculateDiscount();
    }

    private populateProducts = (periods: Period[]): void => {
        for (const period of periods) {
            const product = period.product;
            if (product && !this.products.some(p => p.id === product.id?.replaceAll('-', ''))) {
                this.products.push(product);
            }
        }
        this.products = [...this.products];
    }

    private populateSports = (periods: Period[]): void => {
        for (const period of periods) {
            for (const sport of period.sports) {
                if (!this.sports.find(s => s.id === sport.id?.replaceAll('-', ''))) {
                    this.sports.push(sport);
                }
            }
        }
        this.sports = [...this.sports];
    }

    private populateLevels = (sport: Sport): void => {
        for (const level of sport.levels) {
            if (!this.levels.find(l => l.id === level.id?.replaceAll('-', ''))) {
                this.levels.push(level);
            }
        }
        this.levels = [...this.levels];
    }

    private updateDates = (): void => {
        if (!this.kid || !this.program)
            return;

        if (!this.program.periods)
            return;

        let periods = this.program.periods;
        if (this.product)
            periods = periods.filter(p => p.product?.id === this.product?.id?.replaceAll('-', ''));

        if (this.sport)
            periods = periods.filter(p => p.sports.some(s => s.id === this.sport!.id?.replaceAll('-', '')));

        if (this.level)
            periods = periods.filter(p => p.levels.some(l => l.id === this.level!.id?.replaceAll('-', '')));

        this.requestedDates = [];
        this.requestedPeriodDates = [];

        for (const period of periods) {
            let selected = false;
            if (this.request && this.request.package && this.program.id === this.requestedProgram?.id?.replaceAll('-', '')) {
                selected = this.request.dates.some(d => d.getTime() === period.dates[0].getTime());
            }
            if (period.package) {
                const dateDescriptor: DateDescriptor = {
                    date: period.dates[0],
                    packageDates: [],
                    period: period,
                    showDetails: false,
                    sports: this.sports,
                    levels: this.levels,
                    sport: this.sport,
                    level: this.level,
                    dateIsSelected: selected,
                    discount: this.discount,
                    deposit: period.deposit,
                    depositAmount: 0,
                };
                period.dates.forEach(d => dateDescriptor.packageDates.push({
                    date: d,
                    selected: selected,
                    deposit: period.deposit,
                }));
                this.requestedPeriodDates.push(dateDescriptor);
            } else {
                for (const date of period.dates) {
                    let selected = false;
                    if (this.request && !this.request.package && this.program.id === this.requestedProgram?.id?.replaceAll('-', '')) {
                        selected = this.request.dates.some(d => d.getTime() === date.getTime());
                    }
                    this.requestedDates.push({
                        date: date,
                        packageDates: [],
                        period: period,
                        showDetails: false,
                        sports: this.sports,
                        levels: this.levels,
                        sport: this.sport,
                        level: this.level,
                        dateIsSelected: selected,
                        discount: this.discount,
                        deposit: period.deposit,
                        depositAmount: 0,
                    });
                }
            }
        }

        this.requestedPeriodDates = [...this.requestedPeriodDates];
        this.requestedDates = [...this.requestedDates];
        this.discount = this.calculateDiscount();
    }

    private updateDiscounts = (): void => {
        this.requestedDates.forEach(d => d.discount = this.discount);
        this.requestedPeriodDates.forEach(d => d.discount = this.discount);
    }

    private updatePrices = (dates: DateDescriptor[]): void => {
        dates.forEach(d => {
            if (d.period) {
                const daily = (d.grossPrice ?? d.period.price) / d.period.packageDuration;
                const reduced = this.reducePrice(daily, d.discount);
                if (d.packageDates.length > 0) {
                    d.packageDates.forEach(pd => {
                        pd.netPrice = daily * (1 - d.discount / 100);
                        pd.grossPrice = daily;
                    });
                    const netPrice = d.packageDates.reduce((sum, cur) => sum + (cur.netPrice ?? 0), 0);
                    d.netPrice = Math.round(netPrice);
                    d.grossPrice = d.packageDates.reduce((sum, cur) => sum + (cur.grossPrice ?? 0), 0);
                } else {
                    d.netPrice = reduced;
                    d.grossPrice = d.grossPrice ?? d.period.price;
                }
                d.depositAmount = this.reducePrice(d.grossPrice, 100 - d.deposit);
            } else {
                d.netPrice = undefined;
            }
        });
    }

    private calculateTotalPrice = (dates: DateDescriptor[], packageDates: DateDescriptor[]): number => {
        packageDates.forEach(d => {
            if (d.period) {
                d.netPrice = d.packageDates
                    .filter(d => d.selected)
                    .reduce((sum, current) => sum + (current.netPrice ?? 0), 0);
                d.netPrice = Math.round(d.netPrice + Number.EPSILON);
            } else {
                d.netPrice = undefined;
            }
        });
        const totalFromPackageDates = packageDates.filter(d => d.dateIsSelected).reduce((sum, current) => sum + (current.netPrice ?? 0), 0);
        const totalFromDates = dates.filter(d => d.dateIsSelected).reduce((sum, current) => sum + (current.netPrice ?? 0), 0);
        return totalFromPackageDates + totalFromDates;
    }

    private calculateTotalDeposit = (dates: DateDescriptor[], packageDates: DateDescriptor[]): number => {
        const totalFromPackageDates = packageDates.filter(d => d.dateIsSelected).reduce((sum, current) => sum + (current.depositAmount ?? 0), 0);
        const totalFromDates = dates.filter(d => d.dateIsSelected).reduce((sum, current) => sum + (current.depositAmount ?? 0), 0);
        return totalFromPackageDates + totalFromDates;
    }

    private reducePrice = (price: number, discount: number): number => {
        return Math.round(price * (1 - discount / 100) + Number.EPSILON);
    }
}
